import { memo, useMemo } from "react";
import {
  defineLabels,
  resolveLabels,
  firstError,
  unwrap,
  UpdatingPill,
  Widget,
  WidgetState,
  MetricValue,
  WidgetIssueState,
} from "@nexus/widget-sdk";
import type { MetricValueState } from "@nexus/widget-sdk";
import type { WidgetManifest, WidgetModule, WidgetProps } from "@nexus/widget-sdk";
import { InfoTip } from "@nexus/ui";
import type { KpiStatisticItem, KpiStatisticsConfig, KpiStatisticsData, KpiTone } from "./types";

export type { KpiStatisticItem, KpiStatisticsConfig, KpiStatisticsData, KpiTone } from "./types";

export const LABELS = defineLabels([
  { key: "emptyTitle", name: "Empty state title", default: "No KPI data" },
  {
    key: "emptyDescription",
    name: "Empty state description",
    default: "No KPI statistics available for the selected scope.",
  },
  {
    key: "dataUnavailable",
    name: "Caption under a KPI without data",
    default: "Data unavailable",
  },
  {
    key: "toneDanger",
    name: "Caption for a KPI in danger state",
    default: "Needs attention",
  },
  {
    key: "toneWarning",
    name: "Caption for a KPI in warning state",
    default: "Review required",
  },
  {
    key: "toneSuccess",
    name: "Caption for a KPI in success state",
    default: "Within configured threshold",
  },
] as const);

export const manifest: WidgetManifest = {
  id: "kpi-statistics",
  name: "KPI Statistics",
  description: "Generic KPI tiles for the selected analytics scope.",
  version: "1.1.2",
  labels: LABELS,
  dataRequests: {
    statistics: {
      source: "kpi-statistics",
      contract: { name: "kpi-statistics", version: 1 },
      configurable: false,
    },
  },
};

export const defaultConfig: KpiStatisticsConfig = {
  title: "KPI Statistics",
};

const EMPTY_ITEMS: KpiStatisticItem[] = [];

// Maps KpiTone to MetricValue semantic state. Tone comes from the DataRegistry
// threshold logic; state maps 1-to-1 to the allowed MetricValue states.
// At rest all KPIs default to 'neutral' (text-primary). Threshold breaches drive
// the tone to warning/danger/success — set those thresholds in DataRegistry.
const TONE_STATE_MAP: Record<KpiTone, MetricValueState> = {
  neutral: "neutral",
  success: "ok",
  warning: "warn",
  danger: "error",
};

/**
 * Responsive KPI strip.
 *
 * A plain `auto-fit` + `minmax(MIN, 1fr)` grid fits as many equal columns as will hold
 * MIN, which at intermediate widths produces ODD rows — e.g. four tiles landing as 3 + 1.
 * To keep rows even we drive the layout off the container width instead (Tailwind v4
 * container queries; the strip wrapper carries `@container`):
 *
 *   - Default: exactly 2 columns, so four tiles always pair up as 2 + 2.
 *   - `@4xl` (56rem, i.e. 4 × KPI_MIN_COLUMN): the container is wide enough to hold every
 *     tile at MIN, so `auto-fit` collapses them onto a single row.
 *
 * Because the wide step only unlocks once ALL tiles fit, `auto-fit` can only ever resolve
 * to a full single row there — it never lands on the odd 3-column band. Between the two
 * steps the layout stays at 2 columns. This makes the widget agnostic of its caller's
 * layout class: at `xl:col-span-12` on a large screen the tiles sit on one row; at
 * `xl:col-span-6` the half-width container stays at 2 + 2.
 *
 * KPI_MIN_COLUMN is the tuning knob (keep the `@4xl` step ≈ 4 × this value). 14rem is
 * sized so a tile still comfortably fits its label + a text-3xl value, and lets a
 * full-width strip sit on one row from laptop widths up.
 *
 * Tiles are top-aligned so labels and values line up across a row even when one
 * tile carries an extra caption ("Needs attention").
 *
 * The 1px gap over a divider-coloured background draws the separators on both axes.
 */
const STRIP_GRID =
  "grid h-full auto-rows-fr gap-px rounded-lg bg-border/40 grid-cols-1 @sm/widget:grid-cols-2 @4xl/widget:[grid-template-columns:repeat(auto-fit,minmax(var(--widget-kpi-min-column),1fr))]";

function formatItemValue(item: KpiStatisticItem): string {
  if (item.value === null) return "—";
  if (typeof item.value === "string") return item.value;

  const precision = item.precision ?? (item.format === "decimal" ? 1 : 0);
  const numberFormat = new Intl.NumberFormat("en", {
    minimumFractionDigits: 0,
    maximumFractionDigits: precision,
  });

  if (item.format === "compact") {
    const absoluteValue = Math.abs(item.value);
    return absoluteValue >= 1000
      ? new Intl.NumberFormat("en", {
          notation: "compact",
          compactDisplay: "short",
          maximumFractionDigits: precision,
        }).format(item.value)
      : numberFormat.format(item.value);
  }

  if (item.format === "percent") {
    return `${numberFormat.format(item.value * 100)}${item.suffix ?? "%"}`;
  }

  if (item.format === "text") {
    return String(item.value);
  }

  return `${numberFormat.format(item.value)}${item.suffix ?? ""}`;
}

const KpiTile = memo(function KpiTile({
  item,
  labels,
}: {
  item: KpiStatisticItem;
  labels: Record<(typeof LABELS)[number]["key"], string>;
}) {
  const state = TONE_STATE_MAP[item.tone ?? "neutral"];
  const formattedValue = useMemo(() => formatItemValue(item), [item]);

  return (
    <div className="flex min-w-0 flex-col justify-start gap-3 bg-surface density-metric-padding @[96rem]/widget:px-8 @[96rem]/widget:[&_[data-metric-size]]:text-4xl">
      <div className="flex min-w-0 items-start justify-between gap-2">
        <h4
          className="min-w-0 text-metric-label font-medium text-secondary"
          title={item.label}
        >
          {item.label}
        </h4>
        {item.description ? (
          <InfoTip label={`${item.label} — details`}>{item.description}</InfoTip>
        ) : null}
      </div>

      <div className="flex min-w-0 flex-col items-start gap-1">
        <MetricValue state={state} title={typeof item.value === "string" ? item.value : undefined}>
          {formattedValue}
        </MetricValue>
        {item.value === null && (
          <span className="text-metric-detail text-muted">{labels.dataUnavailable}</span>
        )}
        {item.tone && item.tone !== "neutral" && item.value !== null && (
          <span className="text-metric-detail text-secondary">
            {item.tone === "danger"
              ? labels.toneDanger
              : item.tone === "warning"
                ? labels.toneWarning
                : labels.toneSuccess}
          </span>
        )}
        {item.sub !== undefined && (
          <span
            className="text-metric-detail tabular-nums text-muted"
            title={`${item.sub} channel${item.sub === 1 ? "" : "s"} in scope`}
          >
            {item.sub} channel{item.sub === 1 ? "" : "s"}
          </span>
        )}
      </div>
    </div>
  );
});

function TileSkeleton() {
  return (
    <div className="flex min-w-0 flex-col justify-start gap-3 bg-surface density-metric-padding">
      <div className="h-5 w-28 max-w-full rounded skeleton" />
      <div className="flex flex-col items-start gap-1">
        <div className="h-9 w-24 max-w-full rounded skeleton" />
        <div className="h-4 w-20 max-w-full rounded skeleton" />
      </div>
    </div>
  );
}

function KpiStatisticsComponent({ data, config }: WidgetProps<KpiStatisticsConfig>) {
  const labels = resolveLabels(LABELS, config);
  const error = firstError(data, ["statistics"]);
  const resolved = unwrap<KpiStatisticsData>(data.statistics);
  const isLoading = data.statistics?.isLoading ?? false;

  const items = resolved?.items ?? EMPTY_ITEMS;

  if (error) {
    console.error("[KpiStatisticsWidget] Failed to load data:", error);

    return (
      <Widget hideHeader>
        <WidgetIssueState error={error} />
      </Widget>
    );
  }

  const hasItems = items.length > 0;

  // First load (no data yet): skeleton strip instead of the empty state.
  if (!hasItems && isLoading) {
    return (
      <Widget hideHeader>
        <div
          className="@container h-full"
          role="status"
          aria-label="Loading key metrics"
          aria-busy="true"
        >
          <div className={STRIP_GRID}>
            {Array.from({ length: 4 }, (_, index) => (
              <TileSkeleton key={index} />
            ))}
          </div>
        </div>
      </Widget>
    );
  }

  if (!hasItems) {
    return (
      <Widget hideHeader>
        <WidgetState title={labels.emptyTitle} description={labels.emptyDescription} />
      </Widget>
    );
  }

  return (
    <Widget hideHeader>
      <div className="relative @container h-full">
        <div className={STRIP_GRID}>
          {items.map((item) => (
            <KpiTile key={item.id} item={item} labels={labels} />
          ))}
        </div>
        {isLoading && (
          <div className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2">
            <UpdatingPill />
          </div>
        )}
      </div>
    </Widget>
  );
}

export const Component = KpiStatisticsComponent;

const widgetModule: WidgetModule<KpiStatisticsConfig> = {
  manifest,
  Component,
  defaultConfig,
};

export default widgetModule;
